import React, { useState } from "react";
import { type ColumnDef, type PaginationState } from "@tanstack/react-table";

import { TradePositionSideBadge } from "@/components/trade-position-side-badge";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import type { DeploySignal } from "../../../types/strategyDeploy";
import { TradeLogSortableCell } from "../TradeLog/components/tradeLogSortableCell";
import { fmtClock, fmtMoney, fmtPrice, fmtR, signColor } from "./liveStrategiesFormat";
import { SignalStatusPill } from "./SignalCard";

const PAGE_SIZE = 20;

const price = (id: string, title: string, pick: (s: DeploySignal) => number | null | undefined): ColumnDef<DeploySignal, unknown> => ({
  id,
  accessorFn: (s) => pick(s) ?? null,
  header: ({ column }) => <DataTableColumnHeader column={column} title={title} />,
  cell: ({ row }) => <TradeLogSortableCell className="font-medium">{fmtPrice(pick(row.original))}</TradeLogSortableCell>,
});

const COLUMNS: ColumnDef<DeploySignal, unknown>[] = [
  {
    id: "time",
    accessorFn: (s) => Date.parse(s.created_at),
    header: ({ column }) => <DataTableColumnHeader column={column} title="Time" />,
    cell: ({ row }) => <TradeLogSortableCell>{fmtClock(row.original.created_at)}</TradeLogSortableCell>,
  },
  {
    id: "side",
    accessorFn: (s) => s.direction,
    header: "Type",
    enableSorting: false,
    cell: ({ row }) => <TradePositionSideBadge side={row.original.direction === "short" ? "short" : "long"} />,
  },
  price("entry", "Entry", (s) => s.fill_price ?? s.entry_price),
  price("stop", "Stop", (s) => s.stop_price),
  price("target", "Target", (s) => s.target_price),
  {
    id: "size",
    accessorFn: (s) => s.quantity ?? null,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Size" />,
    cell: ({ row }) => <TradeLogSortableCell className="font-medium">{row.original.quantity ?? "N/A"}</TradeLogSortableCell>,
  },
  price("exit", "Exit", (s) => s.exit_price),
  {
    id: "result",
    accessorFn: (s) => (s.status === "closed" ? s.r_multiple ?? null : null),
    header: ({ column }) => <DataTableColumnHeader column={column} title="Result" />,
    cell: ({ row }) => {
      const s = row.original;
      return (
        <TradeLogSortableCell className="font-medium">
          <span style={{ color: signColor(s.r_multiple) }}>
            {s.status === "closed" ? `${fmtR(s.r_multiple)}, ${fmtMoney(s.pnl)}` : "N/A"}
          </span>
        </TradeLogSortableCell>
      );
    },
  },
  {
    id: "status",
    accessorFn: (s) => s.status,
    header: "Status",
    enableSorting: false,
    cell: ({ row }) => <SignalStatusPill signal={row.original} />,
  },
];

/** A deployment's trades in the app's data table, as the Trade Log and the Lab's Trades tab. */
export function LiveTradesTable({ signals }: { signals: DeploySignal[] }): JSX.Element {
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });
  return (
    <div data-live-trades="">
      <DataTable
        columns={COLUMNS}
        data={signals}
        getRowId={(s) => s.id}
        pagination={pagination}
        onPaginationChange={setPagination}
        showPagination={signals.length > PAGE_SIZE}
        tableClassName="dark:!bg-sidebar"
        emptyMessage="No signals yet. The strategy is watching every candle."
      />
    </div>
  );
}
